import {
  startLoading,
  stopLoading,
} from '../../../common/modules/loading/actions'
import { LoadingTypes } from '../../../common/modules/loading/reducer'
import { updateAppAlert } from '../../../common/modules/appAlert/actions'
import { AppDispatch, RootState } from '../../../store'
import { AxiosError, AxiosResponse } from 'axios'
import { AnyAction } from '@reduxjs/toolkit'
import { CombinedFormState, initialState } from './reducer'
import { RequestFailureMessage } from '../../../common/utils/messagesContants'
import { getErrorMessages } from '../../../common/utils/error'
import { errorLogger } from '../../../common/utils/errorLogger'
import { PlanType } from '@labrav/react-components'
import { getCombinedFormData, putCombinedFormData } from '../../api/markeplace'
import { camelize, snakeize } from 'casing'
import { ContractDurationType } from '../../../admin/modules/resaleAuthorizations/reducer'

export enum CombineFormActionTypes {
  SET_COMBINED_FORM_DATA = 'SET_COMBINED_FORM_DATA',
  CLEAR_COMBINED_FORM_DATA = 'CLEAR_COMBINED_FORM_DATA',
}
const processCombinedFormData = (
  data: CombinedFormState,
  productType: PlanType | undefined
) => {
  const { resellerAgreementData, cppoData } = data
  const {
    dimensions,
    currency,
    installmentInfo,
    awsProductId,
    contractDurationTimeUnit,
    contractDurationInMonthsForEndCustomer,
  } = resellerAgreementData
  const { offerExpirationDate } = cppoData
  const { adjustmentPercentage } = cppoData

  const updatedResellerDimensions = dimensions.map(dim => {
    return {
      ...dim,
      price: null,
      currency: currency,
    }
  })
  const updatedResellerInstallments = installmentInfo
    .filter(installment => installment.amount !== 0)
    .map(installment => ({
      ...installment,
      currency: currency,
    }))
  const updatedCppoInstallments = cppoData.installmentInfo
    .filter(installment => installment.amount !== 0)
    .map(installment => ({
      ...installment,
      currency: currency,
    }))
  let contractDurationInMonths = contractDurationInMonthsForEndCustomer
  if (
    contractDurationTimeUnit === 'YEARS' &&
    productType !== PlanType.SAAS_SUBSCRIPTION
  ) {
    contractDurationInMonths =
      (Number(contractDurationInMonthsForEndCustomer) || 0) * 12
  }

  const updatedDimensions = updatedResellerDimensions.map(dim => {
    if (dim.isAdditionalUsageAllowed && dim.prices?.additionalUsagePrice) {
      const increasedPrice =
        dim.prices.additionalUsagePrice *
        (1 + Number(adjustmentPercentage) / 100)

      return {
        ...dim,
        prices: {
          ...dim.prices,
          additionalUsagePrice: increasedPrice,
        },
        price: null,
      }
    } else if (dim.prices?.subscriptionPrice) {
      const increasedPrice =
        dim.prices.subscriptionPrice * (1 + Number(adjustmentPercentage) / 100)

      return {
        ...dim,
        prices: {
          ...dim.prices,
          subscriptionPrice: increasedPrice,
        },
        price: null,
      }
    } else {
      return dim
    }

    return dim
  })

  const updatedData = {
    ...data,
    resellerAgreementData: {
      ...resellerAgreementData,
      dimensions: updatedResellerDimensions,
      agreementExpirationDate: offerExpirationDate,
      contractDurationType:
        productType === PlanType.SAAS_SUBSCRIPTION
          ? ContractDurationType.STANDARD
          : ContractDurationType.CUSTOM,
      discountType:
        productType === PlanType.SAAS_SUBSCRIPTION
          ? 'individual_pricing'
          : 'flexible_payment_terms',

      installmentInfo: updatedResellerInstallments,
      contractDurationInMonthsForEndCustomer: contractDurationInMonths,
    },
    cppoData: {
      ...cppoData,
      dimensions: updatedDimensions,
      installmentInfo: updatedCppoInstallments,
      awsProductId: awsProductId,
      flexiblePaymentTerms:
        productType === PlanType.SAAS_SUBSCRIPTION ? false : true,
      serviceLength: contractDurationInMonths,
    },
  }
  return updatedData
}
const processGetCombinedFormData = (
  data: any,
  productType?: PlanType
): CombinedFormState => {
  const { detailsJson, ...rest } = data

  if (
    detailsJson.resellerAgreementData === null ||
    detailsJson.cppoData === null
  ) {
    return {
      ...rest,
      ...detailsJson,
    }
  }
  const resellerAgreementData = detailsJson?.resellerAgreementData
  let contractDuration =
    resellerAgreementData?.contractDurationInMonthsForEndCustomer
  if (
    resellerAgreementData?.contractDurationTimeUnit === 'YEARS' &&
    resellerAgreementData?.contractDurationInMonthsForEndCustomer &&
    resellerAgreementData.productType !== PlanType.SAAS_SUBSCRIPTION
  ) {
    const months = Number(
      resellerAgreementData.contractDurationInMonthsForEndCustomer
    )
    contractDuration = months / 12
  }

  const updatedDetailsJson = {
    ...detailsJson,
    resellerAgreementData: {
      ...detailsJson.resellerAgreementData,
      contractDurationInMonthsForEndCustomer: contractDuration,
    },
    cppoData: {
      ...detailsJson?.cppoData,
      adjustmentPercentageInputValue: detailsJson.cppoData.adjustmentPercentage,
    },
  }

  const updatedJson = {
    ...rest,
    ...updatedDetailsJson,
  }

  return updatedJson
}

export const saveCombinedFormData =
  (
    combinedFormData: CombinedFormState,
    productType: PlanType | undefined,
    sendPostMessage?: (
      labraResellerId: string,
      labraIsvId: string,
      combinedFormId: string,
      externalId: string
    ) => void
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.COMBINED_FORM_LOADING))
    try {
      const updatedData = processCombinedFormData(combinedFormData, productType)
      const { data } = await putCombinedFormData(snakeize(updatedData))
      dispatch(
        updateAppAlert({
          message: 'Offer saved successfully',
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
      await dispatch(
        getCurrentCombinedFormData(
          updatedData.id as string,
          updatedData.resellerAgreementData.awsProductId as string
        )
      )
      if (sendPostMessage) {
        await sendPostMessage(
          updatedData.labraCpId,
          updatedData.labraIsvId,
          updatedData.id as string,
          updatedData.externalId as string
        )
      }
    } catch (error: any) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.COMBINED_FORM_LOADING))
    }
  }
export const getCurrentCombinedFormData =
  (labraFormId: string, labraProductID: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.COMBINED_FORM_LOADING))
    try {
      const { data } = await getCombinedFormData(labraFormId)
      const updatedData = processGetCombinedFormData(camelize(data))
      if (
        updatedData.resellerAgreementData === null ||
        updatedData.cppoData === null
      ) {
        initialState.status = updatedData.status
        initialState.resellerAgreementData.awsProductId = labraProductID
        dispatch(setCombinedFormData(initialState))
      } else {
        dispatch(setCombinedFormData(updatedData))
      }
    } catch (error: any) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.COMBINED_FORM_LOADING))
    }
  }

export const setCombinedFormData = (data: CombinedFormState) => {
  return {
    type: CombineFormActionTypes.SET_COMBINED_FORM_DATA,
    payload: data,
  }
}
export const clearCombinedFormData = () =>
  ({
    type: CombineFormActionTypes.CLEAR_COMBINED_FORM_DATA,
  }) as unknown as AnyAction
